// Mirrors apps/app1/services/offer_builder.compose_offer_label — keep in step.
// Returns { label } or { error }.

export const AD_KINDS = [
  { value: 'percent_off', label: 'Percent off' },
  { value: 'dollars_off', label: 'Dollars off' },
  { value: 'free_item', label: 'Free item' },
];

export const FINANCE_STYLES = [
  { value: 'no_interest_no_payments', label: 'No interest or payments for N months' },
  { value: 'zero_interest', label: '0% interest for N months' },
  { value: 'low_monthly', label: 'Payments as low as $X/month' },
];

export const APPLIES_TO_SUGGESTIONS = ['Installation', 'Materials', 'Your Project'];

const APPLIES_TO_MAX = 40;

function parseAmount(value, lo, hi, what) {
  const num = Number(String(value ?? '').replace(/[,$%\s]/g, ''));
  if (value === '' || value === null || value === undefined || Number.isNaN(num)) {
    return { error: `Enter a number for the ${what}.` };
  }
  if (num < lo || num > hi) {
    return { error: `The ${what} must be between ${lo.toLocaleString('en-US')} and ${hi.toLocaleString('en-US')}.` };
  }
  return { value: num };
}

function cleanAppliesTo(value) {
  const text = String(value || '').replace(/\s+/g, ' ').trim();
  if (!text) return { error: 'Say what the offer applies to (e.g. Installation).' };
  if (text.length > APPLIES_TO_MAX) {
    return { error: `Keep 'applies to' under ${APPLIES_TO_MAX} characters — put details in the fine print.` };
  }
  if (/\d/.test(text) || /\b(and|or|plus)\b|[+&/]/i.test(text)) {
    return { error: "One offer per box: 'applies to' can't hold a second offer or numbers. Use a custom request instead." };
  }
  return {
    value: text.split(' ').map((w) => (w === w.toUpperCase() ? w : w.charAt(0).toUpperCase() + w.slice(1))).join(' '),
  };
}

const fmt = (n) => n.toLocaleString('en-US', { maximumFractionDigits: 2 });

export function composeOfferLabel(spec) {
  if (!spec || typeof spec !== 'object') return { error: 'Missing offer details.' };
  const { kind } = spec;
  if (kind === 'percent_off') {
    const amt = parseAmount(spec.amount, 1, 100, 'percentage');
    if (amt.error) return amt;
    const what = cleanAppliesTo(spec.applies_to);
    if (what.error) return what;
    return { label: `${fmt(amt.value)}% Off ${what.value}` };
  }
  if (kind === 'dollars_off') {
    const amt = parseAmount(spec.amount, 1, 100000, 'dollar amount');
    if (amt.error) return amt;
    const what = cleanAppliesTo(spec.applies_to);
    if (what.error) return what;
    return { label: `${spec.up_to ? 'Up To ' : ''}$${fmt(amt.value)} Off ${what.value}` };
  }
  if (kind === 'free_item') {
    const what = cleanAppliesTo(spec.applies_to);
    if (what.error) return what;
    return { label: `Free ${what.value}` };
  }
  if (kind === 'financing') {
    const style = spec.finance_style || 'no_interest_no_payments';
    if (style === 'low_monthly') {
      const amt = parseAmount(spec.amount, 1, 10000, 'monthly payment');
      if (amt.error) return amt;
      return { label: `Payments As Low As $${fmt(amt.value)}/Month` };
    }
    const months = parseAmount(spec.months, 1, 240, 'number of months');
    if (months.error) return months;
    if (style === 'zero_interest') return { label: `0% Interest For ${fmt(months.value)} Months` };
    return { label: `${fmt(months.value)} Months No Interest Or Payments` };
  }
  return { error: 'Pick an offer type.' };
}

// The label a wizard entry will run as right now (pending requests run the
// interim library offer until staff approve).
export function liveLabelForEntry(entry) {
  if (!entry) return '';
  const mode = entry.mode || (entry.selected_label ? 'library' : '');
  if (mode === 'library') return entry.selected_label || '';
  if (mode === 'custom') return composeOfferLabel(entry.custom).label || '';
  if (mode === 'request') return entry.interim_label || '';
  return entry.selected_label || entry.custom_label || '';
}
